/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useEffect, useState } from 'react';
import { Modal, Typography, Tab, Button } from '@mui/material';

import { SafePhotoSphereViewer } from './SafePhotoSphereViewer';
import { useTranslations } from 'next-intl';
import {
  ModalHead,
  StyledTabs,
  Tour360Btn,
  Tour360Iframe,
  Tour360IframeBox,
  Tour360ModalBox,
  Tour360ModalContent,
} from './styled';
import { CrossIcon, Icon360 } from '@/components';
import { is3DTour } from '../../utils';

type Image360 = {
  link: string;
  type: string;
  descriptionDefault?: string;
  changeDate?: number | string | Date;
  descriptions?: Record<string, any>;
};

type TourLink = {
  link: string;
  type: string;
  descriptionDefault?: string;
  changeDate?: number | string | Date;
  descriptions?: Record<string, any>;
};

type Tour360ModalProps = {
  image360?: Image360[];
  tourLinks?: TourLink[];
};

export const Tour360Modal: FC<Tour360ModalProps> = ({ image360 = [], tourLinks = [] }) => {
  const t = useTranslations();

  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(0);

  const handleOpenModal = () => setIsOpen(true);
  const handleCloseModal = () => {
    setIsOpen(false);
    setActive(0);
  };

  const tours = (tourLinks ?? []).filter((m) => is3DTour(m.link));

  type Item = ({ type: 'image360' } & Image360) | ({ type: 'tour' } & TourLink);

  const items: Item[] = [
    ...image360.map(({ type: _ignored, ...m }) => ({
      type: 'image360' as const,
      ...m,
    })),
    ...tours.map(({ type: _ignored, ...m }) => ({
      type: 'tour' as const,
      ...m,
    })),
  ];

  useEffect(() => {
    if (active >= items.length) {
      queueMicrotask(() => {
        setActive(Math.max(0, items.length - 1));
      });
    }
  }, [items.length, active]);

  if (!items.length) return null;

  const activeItem = items[active];
  return (
    <>
      <Tour360Btn
        onClick={(e) => {
          e.stopPropagation();
          handleOpenModal();
        }}
        aria-label="Open 3D tour"
      >
        <Icon360 />
      </Tour360Btn>

      <Modal open={isOpen} onClose={handleCloseModal}>
        <Tour360ModalContent>
          <ModalHead>
            <Typography fontSize={24} fontWeight={600} lineHeight="34px">
              {t('housingCardDetails.3dTour')}
            </Typography>
            <Button variant="transparent" onClick={handleCloseModal} aria-label="Close">
              <CrossIcon aria-hidden="true" />
            </Button>
          </ModalHead>

          <StyledTabs
            value={active}
            onChange={(_, val) => setActive(val)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {items.map((it, idx) => (
              <Tab key={idx} label={it.descriptionDefault ?? `${it.type} ${idx + 1}`} />
            ))}
          </StyledTabs>

          <Tour360ModalBox>
            {activeItem.type === 'image360' ? (
              <SafePhotoSphereViewer src={activeItem.link} minHeight={500} key={activeItem.link} />
            ) : (
              <Tour360IframeBox>
                <Tour360Iframe
                  src={activeItem.link}
                  title={activeItem.descriptionDefault ?? '3D tour'}
                  allowFullScreen
                />
                <Button
                  component="a"
                  href={activeItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                  size="small"
                  sx={{
                    width: 'fit-content',
                    height: '40px',
                    marginTop: '8px',
                  }}
                >
                  {t('housingCardDetails.openSeparately')}
                </Button>
              </Tour360IframeBox>
            )}
          </Tour360ModalBox>
        </Tour360ModalContent>
      </Modal>
    </>
  );
};
